import React, { useState } from 'react';
import {
  Box,
  SpaceBetween,
  Checkbox,
  Select,
  Grid,
  Button
} from '@amzn/awsui-components-react';
import HourlyTab from './HourlyTab';
import { getWorkingDayOptions, getCalendarDayOptions } from '../Utils/GeneralUtils';

const Val = ({ children }) => <Box variant="span" fontWeight="bold">{children}</Box>;

const DailyTab = ({ isViewMode, onConfigChange, initialConfig = {} }) => {
  const [dailyWorkingDay, setDailyWorkingDay] = useState(false);
  const [dailyWorkingDayBegin, setDailyWorkingDayBegin] = useState({ label: 'WD-1', value: 'WD-1' });
  const [dailyWorkingDayEnd, setDailyWorkingDayEnd] = useState({ label: 'WD23', value: 'WD23' });
  const [dailyCalendarDay, setDailyCalendarDay] = useState(true);
  const [dailyCalendarDayBegin, setDailyCalendarDayBegin] = useState({ label: '1', value: '1' });
  const [dailyCalendarDayEnd, setDailyCalendarDayEnd] = useState({ label: '31', value: '31' });
  const [dailyEveryXDays, setDailyEveryXDays] = useState(false);
  const [dailyEveryXDaysValue, setDailyEveryXDaysValue] = useState('2');
  const [showHourlyConfig, setShowHourlyConfig] = useState(false);
  const [hourlyConfig, setHourlyConfig] = useState({});

  React.useEffect(() => {
    if (Object.keys(initialConfig).length > 0) {
      setDailyWorkingDay(initialConfig.workingDay || false);
      if (initialConfig.workingDayBegin) setDailyWorkingDayBegin({ label: initialConfig.workingDayBegin, value: initialConfig.workingDayBegin });
      if (initialConfig.workingDayEnd) setDailyWorkingDayEnd({ label: initialConfig.workingDayEnd, value: initialConfig.workingDayEnd });
      setDailyCalendarDay(initialConfig.calendarDay !== undefined ? initialConfig.calendarDay : true);
      if (initialConfig.calendarDayBegin) setDailyCalendarDayBegin({ label: initialConfig.calendarDayBegin, value: initialConfig.calendarDayBegin });
      if (initialConfig.calendarDayEnd) setDailyCalendarDayEnd({ label: initialConfig.calendarDayEnd, value: initialConfig.calendarDayEnd });
      setDailyEveryXDays(initialConfig.everyXDays || false);
      setDailyEveryXDaysValue(initialConfig.everyXDaysValue || '2');
      setShowHourlyConfig(!!initialConfig.hourlyConfig);
      setHourlyConfig(initialConfig.hourlyConfig || {});
    }
  }, [initialConfig]);

  const workingDayOptions = getWorkingDayOptions();
  const calendarDayOptions = getCalendarDayOptions();

  const handleConfigChange = (overrides = {}) => {
    onConfigChange({
      workingDay: dailyWorkingDay,
      workingDayBegin: dailyWorkingDayBegin?.value,
      workingDayEnd: dailyWorkingDayEnd?.value,
      calendarDay: dailyCalendarDay,
      calendarDayBegin: dailyCalendarDayBegin?.value,
      calendarDayEnd: dailyCalendarDayEnd?.value,
      everyXDays: dailyEveryXDays,
      everyXDaysValue: dailyEveryXDaysValue,
      hourlyConfig: showHourlyConfig ? hourlyConfig : null,
      ...overrides
    });
  };

  if (isViewMode) {
    return (
      <SpaceBetween size="s">
        {dailyCalendarDay && (
          <Box>
            <Box variant="awsui-key-label">Calendar day range</Box>
            <span>Day <Val>{dailyCalendarDayBegin?.label}</Val> to day <Val>{dailyCalendarDayEnd?.label}</Val></span>
          </Box>
        )}
        {dailyWorkingDay && (
          <Box>
            <Box variant="awsui-key-label">Working day range</Box>
            <span><Val>{dailyWorkingDayBegin?.label}</Val> to <Val>{dailyWorkingDayEnd?.label}</Val></span>
          </Box>
        )}
        {dailyEveryXDays && (
          <Box>
            <Box variant="awsui-key-label">Recurrence</Box>
            <span>Every <Val>{dailyEveryXDaysValue}</Val> days</span>
          </Box>
        )}
        {showHourlyConfig && hourlyConfig && Object.keys(hourlyConfig).length > 0 && (
          <Box>
            <Box variant="awsui-key-label">Hourly configuration</Box>
            <HourlyTab isViewMode={true} onConfigChange={() => {}} initialConfig={hourlyConfig} />
          </Box>
        )}
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="m">
      <Box>
        <Checkbox
          checked={dailyWorkingDay}
          onChange={({ detail }) => {
            setDailyWorkingDay(detail.checked);
            if (detail.checked) setDailyCalendarDay(false);
            handleConfigChange({ workingDay: detail.checked, calendarDay: detail.checked ? false : dailyCalendarDay });
          }}
          disabled={true}
        >
          Working day
        </Checkbox>
        <Box margin={{ left: 'xxl', top: 'xs' }}>
          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
            <Box padding={{ top: 'xs' }}>Begin On</Box>
            <Select selectedOption={dailyWorkingDayBegin} onChange={({ detail }) => { setDailyWorkingDayBegin(detail.selectedOption); handleConfigChange({ workingDayBegin: detail.selectedOption.value }); }} options={workingDayOptions} disabled={true} />
            <Box padding={{ top: 'xs' }}>End On</Box>
            <Select selectedOption={dailyWorkingDayEnd} onChange={({ detail }) => { setDailyWorkingDayEnd(detail.selectedOption); handleConfigChange({ workingDayEnd: detail.selectedOption.value }); }} options={workingDayOptions} disabled={true} />
          </Grid>
        </Box>
      </Box>

      <Box>
        <Checkbox
          checked={dailyCalendarDay}
          onChange={({ detail }) => {
            if (!detail.checked && !dailyWorkingDay) return;
            setDailyCalendarDay(detail.checked);
            if (detail.checked) setDailyWorkingDay(false);
            handleConfigChange({ calendarDay: detail.checked, workingDay: detail.checked ? false : dailyWorkingDay });
          }}
        >
          Calendar day
        </Checkbox>
        <Box margin={{ left: 'xxl', top: 'xs' }}>
          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
            <Box padding={{ top: 'xs' }}>Begin On</Box>
            <Select selectedOption={dailyCalendarDayBegin} onChange={({ detail }) => { setDailyCalendarDayBegin(detail.selectedOption); handleConfigChange({ calendarDayBegin: detail.selectedOption.value }); }} options={calendarDayOptions} />
            <Box padding={{ top: 'xs' }}>End On</Box>
            <Select selectedOption={dailyCalendarDayEnd} onChange={({ detail }) => { setDailyCalendarDayEnd(detail.selectedOption); handleConfigChange({ calendarDayEnd: detail.selectedOption.value }); }} options={calendarDayOptions} />
          </Grid>
        </Box>
      </Box>

      <Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 8 }]}>
        <Checkbox checked={dailyEveryXDays} onChange={({ detail }) => { setDailyEveryXDays(detail.checked); handleConfigChange({ everyXDays: detail.checked }); }}>Every</Checkbox>
        <Select
          selectedOption={{ label: dailyEveryXDaysValue, value: dailyEveryXDaysValue }}
          onChange={({ detail }) => { setDailyEveryXDaysValue(detail.selectedOption.value); handleConfigChange({ everyXDaysValue: detail.selectedOption.value }); }}
          options={[...Array(30)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
        />
        <Box padding={{ top: 'xs' }}>days</Box>
      </Grid>

      <Box>
        <Button variant="normal" onClick={() => { setShowHourlyConfig(!showHourlyConfig); handleConfigChange({ hourlyConfig: !showHourlyConfig ? hourlyConfig : null }); }}>
          {showHourlyConfig ? 'Hide' : 'Add'} Hourly Configuration
        </Button>
      </Box>

      {showHourlyConfig && (
        <Box padding={{ left: 'l' }}>
          <Box variant="h3">Hourly Configuration</Box>
          <HourlyTab isViewMode={false} onConfigChange={(config) => { setHourlyConfig(config); handleConfigChange({ hourlyConfig: config }); }} initialConfig={initialConfig.hourlyConfig || { option: 'interval', interval: '1', selectedHours: [], rangeStart: '9', rangeEnd: '17', specificMinute: '0' }} />
        </Box>
      )}
    </SpaceBetween>
  );
};

export default DailyTab;
